import { useEffect, useMemo, useRef, useState } from "react";
import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import type { MediaFieldsFragment } from "@/gql/graphql";

export type BlogPreviewMetadata = {
  title: string;
  excerpt: string | null;
  hero_image_url: string | null;
  reading_time_minutes: number | null;
  published_at: string | null;
  body_images: MediaFieldsFragment[];
  author: {
    name: string;
    role: string | null;
    image: MediaFieldsFragment | null;
  } | null;
};

type BodyFormValues = { body_html?: string | null };

type BlogPreviewSnapshot = BlogPreviewMetadata & {
  body_html: string;
};

type Props = {
  metadata: BlogPreviewMetadata;
  debounceMs?: number;
};

export function BlogPreviewIframe({ metadata, debounceMs = 250 }: Props) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [ready, setReady] = useState(false);
  const { values } = useFormState<BodyFormValues>();

  const appUrl = import.meta.env.VITE_APP_URL as string | undefined;
  const previewUrl = appUrl ? `${appUrl}/blog/preview` : null;
  const targetOrigin = appUrl ? new URL(appUrl).origin : "*";

  const snapshot = useMemo<BlogPreviewSnapshot>(
    () => ({
      ...metadata,
      body_html: values.body_html ?? "",
    }),
    [metadata, values.body_html],
  );

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (appUrl && event.origin !== new URL(appUrl).origin) return;
      const data = event.data as { type?: string } | undefined;
      if (data?.type === "blog-preview-ready") setReady(true);
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [appUrl]);

  useEffect(() => {
    if (!ready) return;

    const id = setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage(
        { type: "blog-preview", snapshot },
        targetOrigin,
      );
    }, debounceMs);

    return () => clearTimeout(id);
  }, [snapshot, ready, debounceMs, targetOrigin]);

  if (!previewUrl) {
    return (
      <div className="rounded-md border border-zinc-200 bg-zinc-50 p-6 text-sm text-zinc-600">
        Set <code>VITE_APP_URL</code> to enable the preview iframe.
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      src={previewUrl}
      title="Blog preview"
      className="h-full w-full rounded-md border border-zinc-200 bg-white"
    />
  );
}
